import type { PropsWithChildren } from "react";
import { pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/lib/Option";

import type { KlassProp, PartialCssType } from "@scripts/react/util/classnames";
import { klass, klassProp } from "@scripts/react/util/classnames";

// Keep this type in sync with web/assets/stylesheets/components/_grid.scss
type GridClasses =
  | "input-row"
  | "grid-sx-1"
  | "grid-sx-15"
  | "grid-sx-2"
  | "grid-sx-4"
  | "grid-sa-1"
  | "grid-sa-15"
  | "grid-sa-2"
  | "grid-sx-md-3"
  | "grid-sxy-sm-4-2"
  | "grid-sxy-1-075";

export const Grid = (props: PropsWithChildren<{ klass?: KlassProp, attrs: O.Option<PartialCssType<GridClasses>> }>) => {
  const defaultKlasses = pipe(
    "grid" as const,
    (defaultK) => pipe(
      props.attrs,
      O.fold(
        () => klassProp(defaultK),
        (attrs) => klassProp(defaultK, attrs)
      )
    )
  );

  return (
    <div {...klass(defaultKlasses, props.klass)}>
      {props.children}
    </div>
  );
};

// keep this in sync with $grid-columns in web/assets/stylesheets/_variables.scss
type ColNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20 | 21 | 22 | 23 | 24;

// keep this in sync with $breakpoints in web/assets/stylesheets/_variables.scss
type ColBreakPoint = "sm" | "md" | "lg" | "xl";

export type GridCols = `c-${ColNumber}` | `c-${ColBreakPoint}-${ColNumber}`;

export const GridCol = (props: PropsWithChildren<{ cols: ReadonlyArray<GridCols>, klass?: KlassProp }>) => {
  return (
    <div {...klass(props.cols, props.klass)}>
      {props.children}
    </div>
  );
};

export const TwoColRow = (props: PropsWithChildren<object>) => (
  <GridCol cols={["c-24", "c-md-12"]}>
    {props.children}
  </GridCol>
);
